import React, { useMemo } from "react";
import { Bar } from "react-chartjs-2";
import { PropTypes } from "prop-types";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { computeDropOff } from "../../services/funnelReport.js";

const TRANSITIONS = [
  { from: "step1", to: "step2", desc: "After Info Complete" },
  { from: "step2", to: "step3", desc: "After Slot Picked" },
  { from: "step3", to: "completed", desc: "After Review" },
];

function StepDropOffChart({ counts, loading, error }) {
  const { labels, values, totals } = useMemo(() => {
    const lbls = TRANSITIONS.map((t) => t.desc);
    const vals = TRANSITIONS.map((t) =>
      computeDropOff(counts?.[t.from] ?? 0, counts?.[t.to] ?? 0)
    );
    const tots = TRANSITIONS.map((t) => ({
      from: counts?.[t.from] ?? 0,
      to: counts?.[t.to] ?? 0,
      lost: (counts?.[t.from] ?? 0) - (counts?.[t.to] ?? 0),
    }));
    return { labels: lbls, values: vals, totals: tots };
  }, [counts]);

  const data = {
    labels,
    datasets: [
      {
        label: "Drop-off %",
        data: values.map((v) => (v == null ? 0 : v)),
        backgroundColor: ["#f0a9b6", "#e57f92", "#d6455d"],
        borderRadius: 6,
        borderSkipped: false,
        maxBarThickness: 90,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      title: { display: false },
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const tot = totals[ctx.dataIndex];
            const pct = values[ctx.dataIndex];
            if (pct == null) return "No baseline to compute drop-off";
            return [
              `Drop-off: ${pct.toFixed(1)}%`,
              `Lost: ${tot.lost} of ${tot.from}`,
              `Continued: ${tot.to}`,
            ];
          },
        },
      },
    },
    scales: {
      x: { grid: { display: false } },
      y: {
        beginAtZero: true,
        max: 100,
        ticks: { callback: (v) => `${v}%` },
        grid: { color: "#f1edf3" },
      },
    },
  };

  if (loading) {
    return (
      <div className="lb-chart-state">
        <FontAwesomeIcon spin icon={faSpinner} style={{ fontSize: 22 }} />
        <span>Loading drop-off…</span>
      </div>
    );
  }

  if (error) {
    return <div className="lb-chart-state">Couldn’t load drop-off: {error}</div>;
  }

  if (!counts || !counts.step1) {
    return (
      <div className="lb-chart-state">
        Not enough data to compute drop-off (no step 1 reached).
      </div>
    );
  }

  return (
    <div>
      <div className="lb-chart lb-chart--bars">
        <Bar options={options} data={data} />
      </div>
      <div className="lb-chart-foot">
        {TRANSITIONS.map((t, i) => (
          <div key={t.desc}>
            <div className="lb-chart-foot__label">{t.desc}</div>
            <div className="lb-chart-foot__value">
              {values[i] == null ? "—" : `${values[i].toFixed(1)}%`}
            </div>
            <div className="lb-chart-foot__hint">
              {totals[i].lost} lost / {totals[i].from} reached
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

StepDropOffChart.propTypes = {
  counts: PropTypes.object,
  loading: PropTypes.bool,
  error: PropTypes.string,
};

export default StepDropOffChart;
